import { useEffect, useState } from "react";
import { Link } from "react-router";

const TEAL = "#4abcc9";
const NAVY = "#0d1f2d";

interface Section { id: string; title: string; content: React.ReactNode; }

interface LegalPageProps {
    badge: string;
    title: string;
    subtitle: string;
    icon: string;
    sections: Section[];
}

export function LegalPage({ badge, title, subtitle, icon, sections }: LegalPageProps) {
    const [activeSection, setActiveSection] = useState(sections[0]?.id ?? "");

    useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

    // Highlight nav on scroll
    useEffect(() => {
        const handler = () => {
            for (const s of [...sections].reverse()) {
                const el = document.getElementById(s.id);
                if (el && el.getBoundingClientRect().top <= 140) {
                    setActiveSection(s.id);
                    break;
                }
            }
        };
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, [sections]);

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div style={{ fontFamily: "'Manrope', sans-serif", overflowX: "hidden" }}>
            {/* Responsive overrides */}
            <style>{`
                @media (max-width: 768px) {
                    .legal-sidebar { display: none !important; }
                    .legal-content-wrapper { flex-direction: column !important; }
                    .legal-card { padding: 20px 18px !important; }
                    .legal-hero-icon { width: 64px !important; height: 64px !important; }
                    .legal-hero-icon .material-symbols-outlined { font-size: 32px !important; }
                }
            `}</style>

            {/* ═══ HERO ═══ */}
            <section style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a4a 60%, #0e4a5a 100%)`, padding: "56px 24px 48px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", inset: 0, opacity: 0.07, backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
                <div style={{ position: "absolute", right: "-80px", top: "-80px", width: 400, height: 400, background: "radial-gradient(circle, rgba(74,188,201,0.22) 0%, transparent 65%)", pointerEvents: "none" }} />

                <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2, display: "flex", alignItems: "center", gap: 32, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 280 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                            <div style={{ width: 36, height: 3, background: TEAL, borderRadius: 2 }} />
                            <span style={{ color: TEAL, fontFamily: "'Montserrat',sans-serif", fontSize: "0.78rem", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const }}>
                                {badge}
                            </span>
                        </div>
                        <h1 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "clamp(2rem, 5vw, 3.2rem)", fontWeight: 900, color: "#ffffff", lineHeight: 1.1, margin: "0 0 16px", letterSpacing: "-1px" }}>
                            {title}
                        </h1>
                        <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.95rem", lineHeight: 1.6, maxWidth: 520, margin: 0 }}>
                            {subtitle}
                        </p>
                    </div>

                    {/* Icon card */}
                    <div className="legal-hero-icon" style={{ width: 100, height: 100, borderRadius: 24, background: "rgba(74,188,201,0.12)", border: "1px solid rgba(74,188,201,0.25)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 48, color: TEAL }}>{icon}</span>
                    </div>
                </div>
            </section>

            {/* ═══ CONTENT ═══ */}
            <section style={{ background: "#f8fbff", padding: "60px 24px 80px" }}>
                <div className="legal-content-wrapper" style={{ maxWidth: 1100, margin: "0 auto", display: "flex", gap: 40, alignItems: "flex-start" }}>

                    {/* Sidebar nav */}
                    <aside className="legal-sidebar" style={{ flex: "0 0 220px", position: "sticky", top: 100, display: "flex", flexDirection: "column", gap: 4 }}>
                        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "0.72rem", fontWeight: 700, color: "#9ca3af", letterSpacing: 2, textTransform: "uppercase" as const, margin: "0 0 12px" }}>
                            Contenido
                        </p>
                        {sections.map(s => (
                            <button
                                key={s.id}
                                onClick={() => scrollTo(s.id)}
                                style={{
                                    background: activeSection === s.id ? "rgba(74,188,201,0.1)" : "transparent",
                                    border: "none",
                                    borderLeft: `3px solid ${activeSection === s.id ? TEAL : "transparent"}`,
                                    borderRadius: "0 8px 8px 0",
                                    padding: "8px 12px",
                                    fontFamily: "'Manrope',sans-serif",
                                    fontSize: "0.82rem",
                                    fontWeight: activeSection === s.id ? 700 : 500,
                                    color: activeSection === s.id ? TEAL : "#6b7280",
                                    textAlign: "left" as const,
                                    cursor: "pointer",
                                    transition: "all 0.18s",
                                    lineHeight: 1.4,
                                }}
                            >
                                {s.title}
                            </button>
                        ))}
                    </aside>

                    {/* Main content */}
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 32 }}>
                        {sections.map(s => (
                            <div
                                key={s.id}
                                id={s.id}
                                style={{ background: "white", borderRadius: 16, padding: "32px 36px", border: "1.5px solid #eef2f7", scrollMarginTop: 100 }}
                                className="legal-card"
                            >
                                <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "1.05rem", fontWeight: 700, color: NAVY, margin: "0 0 16px", paddingBottom: 12, borderBottom: `2px solid rgba(74,188,201,0.2)` }}>
                                    {s.title}
                                </h2>
                                <div style={{ fontSize: "0.92rem", lineHeight: 1.8, color: "#4b5563" }}>
                                    {s.content}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
